import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { thumb } from "@/lib/img";

export function StarRating({ value = 0, size = 14, onChange, readOnly = true, testidPrefix = "star" }) {
    const [hover, setHover] = useState(0);
    const display = hover || value;
    return (
        <div className="inline-flex gap-0.5" role="radiogroup" aria-label="rating">
            {[1, 2, 3, 4, 5].map((i) => (
                <button
                    key={i}
                    type="button"
                    disabled={readOnly}
                    data-testid={`${testidPrefix}-${i}`}
                    onMouseEnter={() => !readOnly && setHover(i)}
                    onMouseLeave={() => !readOnly && setHover(0)}
                    onClick={() => !readOnly && onChange?.(i)}
                    className={`${readOnly ? "cursor-default" : "cursor-pointer hover:scale-110 transition-transform"}`}
                    aria-checked={value === i}
                    role="radio"
                >
                    <Star
                        size={size}
                        className={i <= display ? "fill-accent text-accent" : "text-stone-300"}
                    />
                </button>
            ))}
        </div>
    );
}

export default function Reviews({ targetType, targetId, ownerId, testid = "reviews" }) {
    const { user } = useAuth();
    const [state, setState] = useState({ reviews: [], count: 0, average: 0 });
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/reviews/${targetType}/${targetId}`);
            setState(data);
        } catch { /* ignore */ }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [targetType, targetId]);

    const myReview = user ? state.reviews.find((r) => r.user_id === user.user_id) : null;
    const canReview = user && user.user_id !== ownerId;

    const submit = async (e) => {
        e.preventDefault();
        if (!rating) { toast.error("Pick a star rating"); return; }
        setBusy(true);
        try {
            await api.post("/reviews", { target_type: targetType, target_id: targetId, rating, comment });
            toast.success(myReview ? "Review updated" : "Review posted");
            setComment(""); setRating(0);
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid={testid} className="mt-6">
            <div className="flex items-center gap-3">
                <StarRating value={Math.round(state.average)} size={18} />
                <div className="text-sm">
                    <span className="font-display font-black text-lg" data-testid={`${testid}-average`}>
                        {state.average || "—"}
                    </span>
                    <span className="text-muted-foreground ml-2" data-testid={`${testid}-count`}>
                        ({state.count} review{state.count !== 1 ? "s" : ""})
                    </span>
                </div>
            </div>

            {canReview && (
                <form onSubmit={submit} className="mt-4 p-4 rounded-2xl border border-stone-200 bg-card">
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                        {myReview ? "Update your review" : "Leave a review"}
                    </div>
                    <div className="mt-2">
                        <StarRating value={rating || myReview?.rating || 0} size={28} readOnly={false} onChange={setRating} testidPrefix={`${testid}-input-star`} />
                    </div>
                    <Textarea
                        data-testid={`${testid}-comment`}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={myReview?.comment || "Share what you loved (optional)"}
                        className="mt-3"
                    />
                    <Button type="submit" disabled={busy} data-testid={`${testid}-submit`}
                        className="mt-3 rounded-full bg-primary hover:bg-primary/90 hover:text-white">
                        {busy ? "Posting…" : myReview ? "Update review" : "Post review"}
                    </Button>
                </form>
            )}
            {!user && (
                <div className="mt-4 text-sm text-muted-foreground">
                    <Link to="/auth" className="underline">Sign in</Link> to leave a review.
                </div>
            )}

            <div className="mt-6 space-y-4">
                {state.reviews.map((r) => (
                    <div key={r.review_id} data-testid={`review-${r.review_id}`} className="p-4 rounded-2xl border border-stone-200 bg-card">
                        <div className="flex items-center gap-3">
                            {r.user_picture ? (
                                <img loading="lazy" decoding="async" src={thumb(r.user_picture, 200)} alt={r.user_name} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold">{r.user_name?.slice(0,2).toUpperCase()}</div>
                            )}
                            <div className="flex-1">
                                <div className="text-sm font-semibold">{r.user_name}</div>
                                <StarRating value={r.rating} />
                            </div>
                        </div>
                        {r.comment && <p className="text-sm text-muted-foreground mt-2">{r.comment}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
}
